import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronRight, Clock3, LayoutGrid, Send } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { TOOLS, TOOL_CATEGORIES, type ToolCategory } from '@/shared/config/tools';
import { BrandMark } from '@/shared/ui/BrandMark';

export function Sidebar({
  className,
  onNavigate,
}: {
  className?: string;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const activeCategory = TOOLS.find((tool) => tool.path === location.pathname)?.category;
  const [openCategories, setOpenCategories] = useState<Set<ToolCategory>>(
    () => new Set(activeCategory ? [activeCategory] : []),
  );

  useEffect(() => {
    setOpenCategories(new Set(activeCategory ? [activeCategory] : []));
  }, [activeCategory]);

  const toggleCategory = (category: ToolCategory) => {
    setOpenCategories((current) => {
      const next = new Set(current);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  return (
    <div
      data-testid="sidebar"
      className={cn(
        'h-screen w-64 shrink-0 overflow-y-auto border-r bg-card md:sticky md:top-0 md:self-start',
        className,
      )}
    >
      <div className="p-6">
        <Link
          to="/"
          onClick={onNavigate}
          className="flex items-center gap-2 font-bold text-xl mb-6 hover:opacity-80 transition-opacity"
        >
          <BrandMark className="h-6 w-6 text-primary" />
          <span>EasyUtils</span>
        </Link>

        <NavLink
          to="/"
          end
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'mb-4 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-accent',
              isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
            )
          }
        >
          <Clock3 className="h-5 w-5" />
          Quick access
        </NavLink>

        <NavLink
          to="/all-tools"
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'mb-4 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-accent',
              isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
            )
          }
        >
          <LayoutGrid className="h-5 w-5" />
          All tools
        </NavLink>

        <nav className="space-y-1" aria-label="Tool categories">
          {TOOL_CATEGORIES.map((category) => {
            const categoryTools = TOOLS.filter((t) => t.category === category.id);
            if (categoryTools.length === 0) return null;
            const isOpen = openCategories.has(category.id);

            return (
              <div key={category.id}>
                <button
                  type="button"
                  onClick={() => toggleCategory(category.id)}
                  aria-expanded={isOpen}
                  aria-controls={`sidebar-category-${category.id}`}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg bg-transparent px-3 py-2.5 text-left text-sm font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    activeCategory === category.id && 'bg-accent',
                  )}
                >
                  <ChevronRight
                    className={cn(
                      'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                      isOpen && 'rotate-90',
                    )}
                  />
                  <span className="flex-1 leading-tight">{category.name}</span>
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-foreground">
                    {categoryTools.length}
                  </span>
                </button>
                <div
                  id={`sidebar-category-${category.id}`}
                  className={cn('mt-1 space-y-1 pl-2', !isOpen && 'hidden')}
                >
                  {categoryTools.map((tool) => (
                    <NavLink
                      key={tool.id}
                      to={tool.path}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground',
                          isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
                        )
                      }
                    >
                      <tool.icon className="h-4 w-4 shrink-0" />
                      <span className="leading-tight">{tool.name}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="mt-8 pt-6 border-t">
          <a
            href="mailto:support@oxbitlabs.com?subject=Bug Report / Feedback"
            className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <Send className="h-5 w-5" />
            Bug Report & Feedback
          </a>
        </div>
      </div>
    </div>
  );
}
